import { Injectable, inject, Type } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';
import { ConfirmationModalComponent } from '../../../shared/components/modal/confirmation-modal/confirmation-modal.component';
import { CallOverlayService } from '../call-overlay/call-overlay.service';
import { NavigationStart, Router } from '@angular/router';
import { CallHubService } from '../call-hub/call-hub.service';
import { VideoCallModalComponent } from '../../../shared/components/modal/video-call-modal/video-call-modal.component';

export interface ConfirmationModalOptions {
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  modalOptions?: any;
}

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  private modalService = inject(NgbModal);
  private callOverlay = inject(CallOverlayService);
  private callHub = inject(CallHubService);
  private router = inject(Router);
  private modalRefs: NgbModalRef[] = [];
  private modalClosePolicy = new Map<
    NgbModalRef,
    { skipOnNavigation?: boolean }
  >();

  constructor() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.closeAll();
      }
    });
  }

  openModal(content: any, options: any = {}): NgbModalRef {
    try {
      if (
        this.callOverlay?.getActiveValue &&
        this.callOverlay.getActiveValue()
      ) {
        if (!this.callHub.getCallStateValue()?.inCall) {
          this.callOverlay.end();
        }
      }
    } catch {}
    const defaultOptions = { ariaLabelledBy: 'modal-basic-title' };
    (document.activeElement as HTMLElement)?.blur();
    const modalRef = this.modalService.open(content, {
      ...defaultOptions,
      ...options,
    });
    this.trackModal(modalRef);
    return modalRef;
  }

  openConfirmationModal(content: any, options: any = {}): NgbModalRef {
    try {
      if (
        this.callOverlay?.getActiveValue &&
        this.callOverlay.getActiveValue()
      ) {
        if (!this.callHub.getCallStateValue()?.inCall) {
          this.callOverlay.end();
        }
      }
    } catch {}
    const defaultOptions = {
      ariaLabelledBy: 'modal-basic-title',
      backdrop: 'static',
      keyboard: false,
    };
    const modalRef = this.modalService.open(content, {
      ...defaultOptions,
      ...options,
    });
    this.trackModal(modalRef);
    return modalRef;
  }

  confirm(options: ConfirmationModalOptions = {}): Observable<boolean> {
    return new Observable((observer) => {
      try {
        const modalRef = this.modalService.open(ConfirmationModalComponent, {
          keyboard: false,
          ...options.modalOptions,
        });

        this.trackModal(modalRef);

        const componentInstance =
          modalRef.componentInstance as ConfirmationModalComponent;

        if (options.title) componentInstance.title = options.title;
        if (options.message) componentInstance.message = options.message;
        if (options.confirmText)
          componentInstance.confirmText = options.confirmText;
        if (options.cancelText)
          componentInstance.cancelText = options.cancelText;

        componentInstance.onConfirm.subscribe((e: any) => {
          observer.next(e);
          modalRef.close();
        });

        modalRef.result
          .catch(() => observer.next(false))
          .finally(() => {
            try {
              if (
                this.callOverlay?.getActiveValue &&
                this.callOverlay.getActiveValue()
              ) {
                if (!this.callHub.getCallStateValue()?.inCall) {
                  this.callOverlay.end();
                }
              }
            } catch {}
            this.untrackModal(modalRef);
            observer.complete();
          });
      } catch (error) {
        console.error(error);
        observer.next(false);
        observer.complete();
      }
    });
  }

  openComponentModal<T>(component: Type<T>, options: any = {}): NgbModalRef {
    try {
      if (
        this.callOverlay?.getActiveValue &&
        this.callOverlay.getActiveValue()
      ) {
        if (!this.callHub.getCallStateValue()?.inCall) {
          this.callOverlay.end();
        }
      }
    } catch {}
    options.centered = options.centered ?? true;
    const modalRef = this.modalService.open(component, options);
    if (options.data) {
      modalRef.componentInstance.data = options.data;
    }

    if (component === VideoCallModalComponent) {
      this.modalClosePolicy.set(modalRef, { skipOnNavigation: true });
    }

    this.trackModal(modalRef);
    return modalRef;
  }

  closeAll(): void {
    const callActive = !!this.callHub.getCallStateValue()?.inCall;
    this.modalRefs.forEach((ref) => {
      try {
        if (callActive && this.modalClosePolicy.get(ref)?.skipOnNavigation) {
          return;
        }
        ref.dismiss();
      } catch {}
    });
    this.modalRefs = [];
  }

  private trackModal(modalRef: NgbModalRef): void {
    this.modalRefs.push(modalRef);
    modalRef.result.finally(() => {
      try {
        if (
          this.callOverlay?.getActiveValue &&
          this.callOverlay.getActiveValue()
        ) {
          if (!this.callHub.getCallStateValue()?.inCall) {
            this.callOverlay.end();
          }
        }
      } catch {}
      this.untrackModal(modalRef);
    });
  }

  private untrackModal(modalRef: NgbModalRef): void {
    const index = this.modalRefs.indexOf(modalRef);
    if (index !== -1) {
      this.modalRefs.splice(index, 1);
    }
    this.modalClosePolicy.delete(modalRef);
  }
}
